import { useEffect } from 'react';
import { useSafeLayoutEffect, useBooleanEvent, useWrapElement } from 'ariakit-react-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-react-utils/system';
import { addGlobalEventListener } from 'ariakit-utils/events';
import { useDisclosureContent } from '../disclosure/disclosure-content.js';
import { T as TooltipContext } from '../__utils-b06e21f1.js';
import { jsx } from 'react/jsx-runtime';
import { usePortal } from '../portal/portal.js';

/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a tooltip element.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const state = useToolTipState();
 * const props = useTooltip({ state });
 * <TooltipAnchor state={state}>Anchor</TooltipAnchor>
 * <Role {...props}>Tooltip</Role>
 * ```
 */
const useTooltip = createHook(_ref => {
  let {
    state,
    portal = true,
    hideOnEscape = true,
    hideOnControl = false,
    wrapperProps,
    ...props
  } = _ref;
  const popoverRef = state.popoverRef;

  // Makes sure the wrapper element that's passed to popper has the same
  // z-index as the popover element so users only need to set the z-index
  // once.
  useSafeLayoutEffect(() => {
    const wrapper = popoverRef.current;
    const tooltip = state.contentElement;
    if (!wrapper) return;
    if (!tooltip) return;
    wrapper.style.zIndex = getComputedStyle(tooltip).zIndex;
  }, [popoverRef, state.contentElement]);
  const hideOnEscapeProp = useBooleanEvent(hideOnEscape);
  const hideOnControlProp = useBooleanEvent(hideOnControl);

  // Hide on Escape/Control
  useEffect(() => {
    if (!state.open) return;
    return addGlobalEventListener("keydown", event => {
      if (event.defaultPrevented) return;
      const isEscape = event.key === "Escape" && hideOnEscapeProp(event);
      const isControl = event.key === "Control" && hideOnControlProp(event);
      if (isEscape || isControl) {
        state.hide();
      }
    });
  }, [state.open, hideOnEscapeProp, hideOnControlProp, state.hide]);
  props = useWrapElement(props, element => /*#__PURE__*/jsx("div", {
    role: "presentation",
    ...wrapperProps,
    style: {
      position: state.fixed ? "fixed" : "absolute",
      top: 0,
      left: 0,
      ...wrapperProps?.style
    },
    ref: popoverRef,
    children: element
  }), [state.fixed, popoverRef, wrapperProps]);
  props = useWrapElement(props, element => /*#__PURE__*/jsx(TooltipContext.Provider, {
    value: state,
    children: element
  }), [state]);
  props = {
    role: "tooltip",
    ...props
  };
  props = useDisclosureContent({
    state,
    ...props
  });
  props = usePortal({
    portal,
    ...props,
    preserveTabOrder: false
  });
  return props;
});

/**
 * A component that renders a tooltip element.
 * @see https://ariakit.org/components/tooltip
 * @example
 * ```jsx
 * const tooltip = useTooltipState();
 * <TooltipAnchor state={tooltip}>Anchor</TooltipAnchor>
 * <Tooltip state={tooltip}>Tooltip</Tooltip>
 * ```
 */
const Tooltip = createComponent(props => {
  const htmlProps = useTooltip(props);
  return createElement("div", htmlProps);
});
if (process.env.NODE_ENV !== "production") {
  Tooltip.displayName = "Tooltip";
}

export { Tooltip, useTooltip };
